import PropTypes from 'prop-types';
import { Box, Typography, useTheme } from '@mui/material';
import { Surface, Symbols } from 'recharts';

export const CustomLegend = ({
  chartColors,
  chartDatasets,
  disabledDatasetIndices = new Set(),
  onToggleDataset,
  renameMap,
  singleLineLegend = false
}) => {
  const theme = useTheme();
  function paintLegend(value, datasetIndex) {
    const isInactive = disabledDatasetIndices.has(datasetIndex);
    const legendLabel = renameMap?.[value] ?? value;
    const dotColor =
      chartColors?.[value] ?? theme.fuel6Tokens.JddcColorIconSecondary;

    return (
      <Box
        component="button"
        type="button"
        aria-pressed={!isInactive}
        onClick={() => onToggleDataset(datasetIndex)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 0.5,
          cursor: 'pointer',
          border: 0,
          background: 'transparent',
          borderRadius: '999px',
          p: '0.25rem 0.5rem',
          transition: 'background-color 0.2s ease'
        }}
      >
        <Surface height={20} width={20}>
          <Symbols cx={10} cy={10} type="circle" size={100} fill={dotColor} />
          {isInactive && (
            <Symbols
              cx={10}
              cy={10}
              fill={theme.fuel6Tokens.JddcColorBackgroundPrimary}
              size={20}
              type="circle"
            />
          )}
        </Surface>
        <Typography
          sx={{
            fontSize: { xs: '10px', lg: '14px' },
            lineHeight: 1.2,
            textAlign: 'left',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
            textDecoration: isInactive ? 'line-through' : 'none',
            opacity: isInactive ? 0.65 : 1
          }}
        >
          {legendLabel}
        </Typography>
      </Box>
    );
  }

  const list = Object.values(chartDatasets);

  // Group legend items into a fixed 2-row grid, filled column-first, so
  // related pairs (e.g. Grow/Maintain, Control/Limited Use, Reduce/Do not
  // Use) stack in the same column instead of spreading across separate rows.
  const numRows = singleLineLegend || list.length <= 2 ? 1 : 2;
  const numCols = Math.ceil(list.length / numRows);

  return (
    <Box
      sx={{
        display: 'grid',
        width: '100%',
        mt: 2,
        gridAutoFlow: 'column',
        gridTemplateRows: `repeat(${numRows}, auto)`,
        gridTemplateColumns: `repeat(${numCols}, minmax(${singleLineLegend ? 110 : 140}px, 1fr))`,
        columnGap: 2,
        rowGap: 0.5,
        placeItems: 'center',
        justifyContent: 'center',
        '& > *': {
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-start'
        }
      }}
    >
      {list.map((value, datasetIndex) => (
        <Box key={`${value}-${datasetIndex}`}>
          {paintLegend(value, datasetIndex)}
        </Box>
      ))}
    </Box>
  );
};

CustomLegend.propTypes = {
  chartColors: PropTypes.object,
  chartDatasets: PropTypes.object,
  disabledDatasetIndices: PropTypes.instanceOf(Set),
  onToggleDataset: PropTypes.func.isRequired,
  renameMap: PropTypes.object
};
